/**
 * i18next instance — configured with:
 *   - Namespace support (per-slice namespaces; default: 'common')
 *   - Only enabled language packs bundled (via registry.buildResources())
 *   - Fallback language: 'en'
 *   - Missing-key dev warning (fires only in development, never in production)
 *   - Synchronous initialisation (no Suspense required)
 *
 * Import `i18n` from this module to access the configured instance directly.
 * React components should use `useTranslation` from react-i18next instead.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { buildResources, getDefaultLanguage } from './registry';

// ---------------------------------------------------------------------------
// Namespace registry
// ---------------------------------------------------------------------------

/**
 * All registered namespaces.
 *
 * When a feature slice adds its own translation keys it should:
 *   1. Add the namespace name to this array.
 *   2. Create matching locale files under
 *      `shared/i18n/locales/{lang}/{namespace}.json`.
 *   3. Use `useTranslation('my-namespace')` in its components.
 */
export const NAMESPACES = [
  'common',
  'ping',
  'auth',
  'settings',
  'notifications',
  'organizations',
  'invitations',
  'mfa',
  // Phase 1–4 namespaces (lifts, documents, audit)
  'lifts',
  'documents',
  'audit',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

// ---------------------------------------------------------------------------
// Missing-key handler (dev only)
// ---------------------------------------------------------------------------

const isDev = import.meta.env.DEV;

function missingKeyHandler(
  langs: readonly string[],
  ns: string,
  key: string,
  fallbackValue: string,
): void {
  if (isDev) {
    console.warn(
      `[i18n] Missing translation key — lang: ${langs.join(', ')} | ns: ${ns} | key: "${key}" | fallback: "${fallbackValue}"`,
    );
  }
}

// ---------------------------------------------------------------------------
// Initialise i18next
// ---------------------------------------------------------------------------

const i18n = i18next.createInstance();

i18n
  .use(initReactI18next)
  .init({
    resources: buildResources(),
    lng: getDefaultLanguage(),
    fallbackLng: 'de',
    defaultNS: 'common',
    ns: NAMESPACES,

    // Key separator — use '.' for nested JSON keys (e.g. "nav.organizations")
    keySeparator: '.',
    nsSeparator: ':',

    interpolation: {
      // React already escapes output; disable double-escaping
      escapeValue: false,
    },

    // Missing key behaviour
    saveMissing: isDev,
    missingKeyHandler,

    // Synchronous init — resources are pre-bundled, no async loading needed
    initImmediate: false,
  });

export default i18n;
